import React from 'react';
import { ShieldCheck, Leaf, Award } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import halalPutih from '../assets/halal_putih.png';
import durian from '../assets/durian.jpg';

const ValueProposition = () => {
  const { t } = useLanguage();

  const icons = [ShieldCheck, Leaf, Award];

  return (
    <section id="benefits" className="py-20 bg-gradient-to-br from-[#FED09D] to-[#FED09D]/50 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://images.unsplash.com/photo-1506368249639-73a05d6f6488?crop=entropy&cs=srgb&fm=webp&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxuYXR1cmFsJTIwaW5ncmVkaWVudHN8ZW58MHx8fHwxNzc1NzM2OTcwfDA&ixlib=rb-4.1.0&q=80&w=1200"
          alt=""
          width="1200"
          height="800"
          className="w-full h-full object-cover"
          aria-hidden="true"
        />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-[#B22222]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-64 h-64 bg-white/50 rounded-full blur-2xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom duration-700">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#3E2723] mb-4">
            {t.benefits.title}
          </h2>
          <p className="text-lg text-[#3E2723]/70 max-w-2xl mx-auto">
            {t.benefits.subtitle}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {t.benefits.items.map((benefit, index) => {
            const Icon = icons[index];
            return (
              <div
                key={index}
                className="group relative animate-in fade-in slide-in-from-bottom duration-700"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-transparent hover:border-[#B22222]/20">
                  {/* Icon Container */}
                  <div className="mb-6">
                    {index === 0 ? (
                      <div className="w-20 h-20 bg-gradient-to-br from-[#B22222] to-[#8B0000] rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 border border-gray-100">
                        <img src={halalPutih} alt="Halal Indonesia" className="w-12 h-12 object-contain" />
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-gradient-to-br from-[#B22222] to-[#8B0000] rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                        <Icon className="text-white" size={36} />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-[#3E2723] mb-4 group-hover:text-[#B22222] transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-[#3E2723]/70 leading-relaxed">
                    {benefit.description}
                  </p>

                  {/* Decorative Element */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-[#B22222]/5 rounded-full blur-2xl group-hover:bg-[#B22222]/10 transition-colors duration-500 -z-10"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Trust Section */}
        <div className="mt-20 bg-white rounded-3xl p-8 md:p-12 shadow-xl animate-in fade-in slide-in-from-bottom duration-700 delay-500">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <img
                src={durian}
                alt="Durian Bakpia"
                className="rounded-2xl shadow-lg w-full h-64 object-cover"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-[#3E2723] mb-4">
                {t.benefits.trust.title}
              </h3>
              <p className="text-[#3E2723]/70 leading-relaxed mb-6">
                {t.benefits.trust.description}
              </p>
              <div className="flex flex-wrap gap-4">
                {t.benefits.trust.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-[#FED09D] px-4 py-2 rounded-full">
                    <span className="text-2xl">{index === 0 ? '👨‍🍳' : '⏰'}</span>
                    <span className="text-sm font-medium text-[#3E2723]">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;
